/**
 * org-detail-page.tsx — Chi tiết 1 tổ chức (Platform). Route `/platform/companies/:orgId?tab=…`.
 *
 * Các tab (mỗi tab một query riêng, chỉ tải khi mở — Radix Tabs unmount tab ẩn):
 *   Tổng quan · Gói & thanh toán · Nhân viên · Kênh · Mức sử dụng · Nhật ký · Cài đặt
 * API: backend `platform/org-manage-routes.ts` (/platform/orgs/:orgId/*). Thao tác ghi đều
 * có lý do và vào nhật ký Platform.
 */
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useOrgOverview } from '@/hooks/use-platform-org-manage'
import { OrgStatusBadge, formatDate } from './org-status'
import { OverviewTab } from './org-detail/overview-tab'
import { BillingTab } from './org-detail/billing-tab'
import { MembersTab } from './org-detail/members-tab'
import { ChannelsTab } from './org-detail/channels-tab'
import { UsageTab } from './org-detail/usage-tab'
import { LogsTab } from './org-detail/logs-tab'
import { SettingsTab } from './org-detail/settings-tab'
import { DeletedOrgBanner } from './org-detail/delete-org'

const TABS = [
  { id: 'overview', label: 'Tổng quan' },
  { id: 'billing', label: 'Gói & thanh toán' },
  { id: 'members', label: 'Nhân viên' },
  { id: 'channels', label: 'Kênh' },
  { id: 'usage', label: 'Mức sử dụng' },
  { id: 'logs', label: 'Nhật ký' },
  { id: 'settings', label: 'Cài đặt' },
] as const
type TabId = (typeof TABS)[number]['id']

export function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data, isLoading, isError } = useOrgOverview(orgId ?? '')
  const tab: TabId = TABS.some((t) => t.id === params.get('tab')) ? (params.get('tab') as TabId) : 'overview'

  if (!orgId) return <ErrorState message="Không tìm thấy tổ chức." />
  if (isLoading) return <Loading label="Đang tải..." />
  if (isError || !data) return <ErrorState message="Không tìm thấy tổ chức." />
  const { org, billing } = data

  return (
    <div className="space-y-5">
      <PageHeader
        title={org.name}
        description="Quản lý tổ chức: gói, thanh toán, nhân viên, kênh, mức sử dụng và nhật ký."
        actions={
          <Button variant="outline" onClick={() => navigate(org.deletedAt ? '/platform/companies?view=deleted' : '/platform/companies')}>
            <ArrowLeft className="h-4 w-4" /> Danh sách
          </Button>
        }
      />

      <DeletedOrgBanner org={org} />

      <div className="flex flex-wrap items-center gap-2">
        {org.deletedAt && <Badge variant="destructive">Đã xoá</Badge>}
        <OrgStatusBadge status={org.displayStatus} />
        <Badge variant="outline">Gói: {billing?.planName ?? org.plan ?? 'Miễn phí'}</Badge>
        {billing?.wasDowngraded && <Badge variant="warning">Gói trả phí đã hết hạn</Badge>}
        <Badge variant="outline">Hết hạn: {formatDate(org.expiresAt)}</Badge>
        {org.expiringSoon && <Badge variant="warning">Sắp hết hạn</Badge>}
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          const next = new URLSearchParams(params)
          if (v === 'overview') next.delete('tab')
          else next.set('tab', v)
          setParams(next, { replace: true })
        }}
      >
        <div className="overflow-x-auto">
          <TabsList>
            {TABS.map((t) => <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>)}
          </TabsList>
        </div>
        <TabsContent value="overview"><OverviewTab orgId={orgId} /></TabsContent>
        <TabsContent value="billing"><BillingTab orgId={orgId} /></TabsContent>
        <TabsContent value="members"><MembersTab orgId={orgId} /></TabsContent>
        <TabsContent value="channels"><ChannelsTab orgId={orgId} /></TabsContent>
        <TabsContent value="usage"><UsageTab orgId={orgId} /></TabsContent>
        <TabsContent value="logs"><LogsTab orgId={orgId} /></TabsContent>
        <TabsContent value="settings"><SettingsTab orgId={orgId} /></TabsContent>
      </Tabs>
    </div>
  )
}
